import { useState, useEffect, useContext } from 'react';
import { Table, Button, Input, Form, Modal, Select, Spin, Popconfirm, TreeSelect, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from 'axios';
import { LoginContext } from '../../../context/loginContext';

const { Option } = Select;
const { TreeNode } = TreeSelect;

const CPlanCuentas = () => {
    const [planes, setPlanes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingPlan, setEditingPlan] = useState(null);
    const [form] = Form.useForm();
    const { user,selectedEmpresa } = useContext(LoginContext);

    useEffect(() => {
        fetchPlanes();
    }, []);

    const fetchPlanes = async () => {
        setLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/plan-cuenta/${selectedEmpresa.emp_id}`;
            const config = {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                },
            };
            const response = await axios.get(url, config);
            // Organizamos las cuentas por nivel y padre para mostrarlas jerárquicamente
            const planesOrganizados = organizarPlanes(response.data);
            console.log('planes organizados', planesOrganizados)
            setPlanes(planesOrganizados);
        } catch (error) {
            console.error('Error obteniendo el plan de cuentas:', error);
            message.error('Error al cargar el plan de cuentas');
        } finally {
            setLoading(false);
        }
    };

    // Función para organizar las cuentas en una estructura jerárquica
    const organizarPlanes = (datos) => {
        if (!Array.isArray(datos)) return [];
        
        // Crear un mapa de cuentas por su ID
        const planesMap = {};
        datos.forEach(plan => {
            planesMap[plan.pla_id] = {
                ...plan,
                key: plan.pla_id,
                children: []
            };
        });
        
        // Organizar en estructura jerárquica
        const result = [];
        datos.forEach(plan => {
            if (plan.pla_padre) {
                // Si tiene padre, agregar como hijo
                if (planesMap[plan.pla_padre]) {
                    planesMap[plan.pla_padre].children.push(planesMap[plan.pla_id]);
                }
            } else {
                // Si no tiene padre, es una cuenta de nivel superior
                result.push(planesMap[plan.pla_id]);
            }
        });
        
        return result;
    };

    const handleAdd = () => {
        setEditingPlan(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingPlan(record);
        form.setFieldsValue({
            pla_codigo: record.pla_codigo,
            pla_nombre: record.pla_nombre,
            pla_tipo: record.pla_tipo,
            pla_nivel: record.pla_nivel,
            pla_padre: record.pla_padre
        });
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const url = `${process.env.REACT_APP_API_URL}/plan-cuenta/${id}`;
            const config = {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                },
            };
            await axios.delete(url, config);
            message.success('Cuenta eliminada correctamente');
            fetchPlanes();
        } catch (error) {
            console.error('Error eliminando la cuenta:', error);
            message.error('Error al eliminar la cuenta');
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const data = {
                ...values,
                pla_empresa: selectedEmpresa?.emp_id // Se usa selectedEmpresa en lugar de user
            };
    
            if (editingPlan) {
                // Actualizar
                const url = `${process.env.REACT_APP_API_URL}/plan-cuenta/${editingPlan.pla_id}`;
                const config = {
                    headers: {
                        Authorization: `Bearer ${user.token}`,
                    },
                };
                await axios.put(url, data, config);
                message.success('Cuenta actualizada correctamente');
            } else {
                // Crear nueva
                const url = `${process.env.REACT_APP_API_URL}/plan-cuenta`;
                const config = {
                    headers: {
                        Authorization: `Bearer ${user.token}`,
                    },
                };
                await axios.post(url, data, config);
                message.success('Cuenta creada correctamente');
            }
    
            setModalVisible(false);
            fetchPlanes();
        } catch (error) {
            console.error('Error guardando la cuenta:', error);
            message.error('Error al guardar la cuenta');
        }
    };
    

    // Genera el TreeSelect con las cuentas disponibles para seleccionar como padre
    const renderTreeNodes = (data) => {
        return data.map(item => {
            if (item.children && item.children.length > 0) {
                return (
                    <TreeNode value={item.pla_id} title={`${item.pla_codigo} - ${item.pla_nombre}`} key={item.pla_id}>
                        {renderTreeNodes(item.children)}
                    </TreeNode>
                );
            }
            return <TreeNode value={item.pla_id} title={`${item.pla_codigo} - ${item.pla_nombre}`} key={item.pla_id} />;
        });
    };

    const columns = [
        {
            title: 'Código',
            dataIndex: 'pla_codigo',
            key: 'pla_codigo',
        },
        {
            title: 'Nombre',
            dataIndex: 'pla_nombre',
            key: 'pla_nombre',
        },
        {
            title: 'Tipo',
            dataIndex: 'pla_tipo',
            key: 'pla_tipo',
        },
        {
            title: 'Nivel',
            dataIndex: 'pla_nivel',
            key: 'pla_nivel',
        },
        {
            title: 'Acciones',
            key: 'action',
            render: (_, record) => (
                <>
                    <Button 
                        type="primary" 
                        icon={<EditOutlined />} 
                        onClick={() => handleEdit(record)}
                        style={{ marginRight: 8 }}
                    />
                    <Popconfirm
                        title="¿Está seguro de eliminar esta cuenta?"
                        onConfirm={() => handleDelete(record.pla_id)}
                        okText="Sí"
                        cancelText="No"
                    >
                        <Button danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </>
            ),
        },
    ];

    return (
        <div>
            <h1>Plan de Cuentas</h1>
            
            <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={handleAdd}
                style={{ marginBottom: 16 }}
            >
                Agregar Cuenta
            </Button>

            {loading ? (
                <div style={{ textAlign: 'center', margin: '20px 0' }}>
                    <Spin size="large" />
                </div>
            ) : (
                <Table 
                    dataSource={planes} 
                    columns={columns} 
                    rowKey="pla_id"
                    pagination={false}
                    expandable={{
                        defaultExpandAllRows: true
                    }}
                />
            )}

            {/* Modal para crear/editar cuentas */}
            <Modal
                title={editingPlan ? "Editar Cuenta" : "Nueva Cuenta"}
                visible={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
                okText={editingPlan ? "Actualizar" : "Crear"}
                cancelText="Cancelar"
            >
                <Form
                    form={form}
                    layout="vertical"
                >
                    <Form.Item
                        name="pla_codigo"
                        label="Código"
                        rules={[{ required: true, message: 'Por favor ingrese el código' }]}
                    >
                        <Input placeholder="Ej. 1.1.01" />
                    </Form.Item>

                    <Form.Item
                        name="pla_nombre"
                        label="Nombre"
                        rules={[{ required: true, message: 'Por favor ingrese el nombre' }]}
                    >
                        <Input placeholder="Nombre de la cuenta" />
                    </Form.Item>

                    <Form.Item
                        name="pla_tipo"
                        label="Tipo"
                        rules={[{ required: true, message: 'Por favor seleccione el tipo' }]}
                    >
                        <Select placeholder="Seleccione un tipo">
                            <Option value="ACTIVO">Activo</Option>
                            <Option value="PASIVO">Pasivo</Option>
                            <Option value="PATRIMONIO">Patrimonio</Option>
                            <Option value="INGRESOS">Ingresos</Option>
                            <Option value="EGRESOS">Egresos</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="pla_nivel"
                        label="Nivel"
                        rules={[{ required: true, message: 'Por favor ingrese el nivel' }]}
                    >
                        <Select placeholder="Seleccione un nivel">
                            <Option value="1">1</Option>
                            <Option value="2">2</Option>
                            <Option value="3">3</Option>
                            <Option value="4">4</Option>
                            <Option value="5">5</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="pla_padre"
                        label="Cuenta Padre"
                    >
                        <TreeSelect
                            placeholder="Seleccione una cuenta padre (opcional)"
                            allowClear
                            treeDefaultExpandAll
                        >
                            {renderTreeNodes(planes)}
                        </TreeSelect>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default CPlanCuentas;